// auth.guard.ts - Updated with loading state and login redirect prevention
import { Injectable } from '@angular/core';
import { CanActivate, ActivatedRouteSnapshot, RouterStateSnapshot, Router } from '@angular/router';
import { Observable, of } from 'rxjs';
import { map, catchError, tap } from 'rxjs/operators';
import { AuthService } from './shared/services/auth.service'; // Update path as needed

@Injectable({
  providedIn: 'root'
})
export class AuthGuard implements CanActivate {

  constructor(
    private authService: AuthService,
    private router: Router
  ) { }

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean> {
    const url = state.url;

    console.log("canActivate  Called");
    const isPublic = route.data['public'] === true;
    console.log("isPublic", isPublic);
    if (isPublic) {
      return of(true); // allow navigation
    }

    // NEW: Check if user is trying to access login/auth pages while logged in
    if (this.isAuthPage(url) && this.authService.isAuthenticated()) {
      return this.authService.getUserDetails().pipe(
        map((user: any) => {
          if (user && user.role_name) {
            // Redirect logged-in user to their dashboard
            this.redirectToUserDashboard(user.role_name);
            return false;
          }
          return true; // Allow access if no user details
        }),
        catchError(() => of(true)) // Allow access on error
      );
    }

    // Check if route requires authentication
    if (!this.requiresAuth(url)) {
      return of(true);
    }

    // Check if user is authenticated
    if (!this.authService.isAuthenticated()) {
      // Store attempted URL for redirect after login
      if (typeof window !== 'undefined' && window.localStorage) {
        localStorage.setItem('redirect_url', url);
      }
      console.log("this.authService.isAuthenticated()", this.authService.isAuthenticated())
      this.router.navigate(['/login']);
      return of(false);
    }

    // NEW: Show loading state before checking user details
    this.showLoadingState();
console.log("GET USER DETAIL CHECKED IN AUTH GUARD")
    // Get user details and check permissions
    return this.authService.getUserDetails().pipe(
      map((user: any) => {
        // Hide loading state
        this.hideLoadingState();

        if (!user || !user.role_name) {
          this.router.navigate(['/login']);
          return false;
        }

        // Check if user can access this route
        if (this.canAccessRoute(url, user.role_name)) {
          return true;
        } else {
          // Redirect to appropriate dashboard
          this.redirectToUserDashboard(user.role_name);
          return false;
        }
      }),
      catchError(() => {
        this.hideLoadingState();
        this.router.navigate(['/login']);
        return of(false);
      })
    );
  }

  // NEW: Check if URL is an authentication page
  private isAuthPage(url: string): boolean {
    const authPages = ['/login', '/sign-up', '/reset-password'];
    return authPages.some(page => url.startsWith(page));
  }

  // NEW: Show loading state (you can customize this)
  private showLoadingState(): void {
    // Add loading indicator to body or create a service for this
    const loadingEl = document.createElement('div');
    loadingEl.id = 'auth-loading';
    loadingEl.innerHTML = `
      <div style="
        position: fixed; 
        top: 0; 
        left: 0; 
        width: 100%; 
        height: 100%; 
        background: rgba(255, 255, 255, 0.9); 
        display: flex; 
        justify-content: center; 
        align-items: center; 
        z-index: 9999;
        font-family: 'Poppins', Arial, sans-serif;
      ">
        <div style="text-align: center;">
          <div style="
            width: 40px; 
            height: 40px; 
            border: 4px solid #f3f3f3; 
            border-top: 4px solid #3498db; 
            border-radius: 50%; 
            animation: spin 1s linear infinite;
            margin: 0 auto 16px;
          "></div>
          <div>Loading...</div>
        </div>
      </div>
      <style>
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      </style>
    `;
    document.body.appendChild(loadingEl);
  }

  // NEW: Hide loading state
  private hideLoadingState(): void {
    const loadingEl = document.getElementById('auth-loading');
    if (loadingEl) {
      loadingEl.remove();
    }
  }

  private requiresAuth(url: string): boolean {
    // Only admin routes require authentication
    return url.startsWith('/admin/');
  }

  private canAccessRoute(url: string, userRole: string): boolean {
    // Admin can access everything
    if (userRole === 'admin') {
      return true;
    }

    // Customer routes
    if (url.startsWith('/admin/customer/')) {
      return userRole === 'customer';
    }

    // Business routes
    if (url.startsWith('/admin/business/')) {
      return userRole === 'business_owner';
    }

    // Staff routes
    if (url.startsWith('/admin/staff/')) {
      return userRole === 'staff';
    }

    // Default: deny access to unknown admin routes
    if (url.startsWith('/admin/')) {
      return false;
    }

    // Allow access to public routes
    return true;
  }

  private redirectToUserDashboard(userRole: string): void {
    switch (userRole) {
      case 'customer':
        this.router.navigate(['/admin/customer/overview']);
        break;
      case 'business_owner':
        this.router.navigate(['/admin/business/overview']);
        break;
      case 'staff':
        this.router.navigate(['/admin/staff/overview']);
        break;
      case 'admin':
        this.router.navigate(['/admin/business/overview']);
        break;
      default:
        this.router.navigate(['/login']);
    }
  }
}

// auth-redirect.helper.ts - Updated helper for login redirects
export class AuthRedirectHelper {
  static redirectAfterLogin(router: any, userRole: string): void {
    // Check for stored redirect URL
    const redirectUrl = localStorage.getItem('redirect_url');

    if (redirectUrl) {
      localStorage.removeItem('redirect_url');
      router.navigateByUrl(redirectUrl);
      return;
    }

    // Default redirects
    switch (userRole) {
      case 'customer':
        router.navigate(['/admin/customer/overview']);
        break;
      case 'business_owner':
        router.navigate(['/admin/business/overview']);
        break;
      case 'staff':
        router.navigate(['/admin/staff/overview']);
        break;
      case 'admin':
        router.navigate(['/admin/business/overview']);
        break;
      default:
        router.navigate(['/customer']);
    }
  }
}